import {useMutation} from "@apollo/client";
import * as Sentry from "@sentry/react";
import {useRef} from "react";
import {toast} from "react-hot-toast";
import {Link} from "react-router";

import {default as Button, default as button} from "@/components/button/Button.tsx";
import {ButtonProps, ButtonVariant} from "@/components/button/Button.types.ts";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {
  GQL_PRIVATE_LOCATIONS_LIST_QUERY,
  GQL_PROVISION_PRIVATE_LOCATION,
  ProvisionPrivateLocationResult,
} from "@/lib/queries/monitoring.ts";
import {AccountSubscriptionType} from "@/lib/types/accounts.ts";
import {Plan} from "@/lib/types/billing.ts";
import {LimitCheckerComponent} from "@/pages/billing/v2/InstantCheckout/LimitCheckWithInstantCheckout.tsx";
import {PrivateLocationsCheckAndUpgrade} from "@/pages/monitoring/privatelocations/list/PrivateLocationsCheckAndUpgrade.tsx";

interface DeployPrivateLocationButtonProps {
  privateLocationsAvailable: number;
  variant?: ButtonVariant;
}

export const DeployPrivateLocationButton = ({privateLocationsAvailable, variant}: DeployPrivateLocationButtonProps) => {
  const privateLocationsLimitCheckerRef = useRef<LimitCheckerComponent>();

  const [provisionPrivateLocation] = useMutation<ProvisionPrivateLocationResult>(GQL_PROVISION_PRIVATE_LOCATION, {
    refetchQueries: [GQL_PRIVATE_LOCATIONS_LIST_QUERY],
  });

  const {user} = useCurrentUserContext();

  const sharedButtonProps: ButtonProps<typeof button> = {
    variant: "primary",
    size: "large",
  };

  // provisioning is gated to administrators on the server, so the control is not offered to others
  if (!user?.has_admin_access) {
    return null;
  }

  if (
    user?.account.free_trial_expires_at ||
    user?.account.plan === Plan.Starter ||
    (privateLocationsAvailable === 0 && user?.account?.subscription_type === AccountSubscriptionType.BILLING_1_0)
  ) {
    return (
      <Button as={Link} to="/billing/manage-subscription" {...sharedButtonProps}>
        Upgrade to {user?.account.plan === Plan.Starter ? "Use" : "Add"} Private Locations
      </Button>
    );
  }

  return (
    <>
      <PrivateLocationsCheckAndUpgrade ref={privateLocationsLimitCheckerRef} />
      <Button
        variant={variant}
        onClick={async () => {
          if (!(await privateLocationsLimitCheckerRef.current?.checkIfLimitHit())) {
            try {
              await provisionPrivateLocation();
            } catch (error) {
              toast.error("Error provisioning private location, please contact support");
              Sentry.captureException(error, {data: "Failed to provision private location"});
            }
          }
        }}
        {...sharedButtonProps}
      >
        Deploy Private Location
      </Button>
    </>
  );
};
